import React from "react";
import { Globe, Shield, LogOut, Plus } from "lucide-react";
import { useAuth } from "../../hooks/useAuth.tsx";

interface NoWebsitesEmptyStateProps {
  onAddWebsite?: () => void;
  onSwitchToDemo?: () => void;
  onRefreshWebsites?: () => void;
}

export const NoWebsitesEmptyState: React.FC<NoWebsitesEmptyStateProps> = ({
  onAddWebsite,
  onSwitchToDemo,
  onRefreshWebsites,
}) => {
  const { account, logout } = useAuth();

  return (
    <div className="min-h-screen bg-canvas flex flex-col font-sans text-foreground">
      {/* Top Header */}
      <header className="h-12 border-b border-border bg-panel px-4 flex items-center justify-between select-none">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-6 h-6 rounded bg-brand-subtle text-brand border border-brand-border">
            <Shield className="w-4 h-4" />
          </div>
          <span className="text-sm font-semibold text-foreground">
            AIShield Console
          </span>
          <span className="text-[11px] font-mono px-1.5 py-0.2 rounded bg-panel-subtle text-foreground-muted border border-border">
            Customer Portal
          </span>
        </div>

        <div className="flex items-center gap-3">
          {account && (
            <span className="text-xs font-mono text-foreground-muted hidden sm:inline">
              {account.email}
            </span>
          )}
          <button
            type="button"
            onClick={() => logout()}
            className="flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded bg-canvas hover:bg-panel-hover border border-border text-foreground-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Sign Out</span>
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-grow flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-lg bg-panel border border-border rounded-lg p-6 sm:p-8 shadow-sm">
          {/* Icon & Title */}
          <div className="w-12 h-12 rounded-xl bg-panel-subtle border border-border flex items-center justify-center mb-5 text-brand">
            <Globe className="w-6 h-6" />
          </div>

          <h2 className="text-base font-semibold text-foreground mb-1.5">
            No Websites Configured
          </h2>
          <p className="text-xs text-foreground-muted leading-relaxed mb-6">
            Website onboarding is the next step. Register your website and verify ownership to enable real-time AI security proxying and telemetry.
          </p>

          {/* Account Metadata Panel */}
          <div className="bg-canvas border border-border rounded-md p-4 mb-6 space-y-2.5 font-mono text-xs">
            <div className="flex items-center justify-between text-[11px] text-foreground-muted border-b border-border/50 pb-2">
              <span className="uppercase tracking-wider">Account Identity</span>
              <span className="text-emerald-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                ACTIVE
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground-dim">Email</span>
              <span className="text-foreground">{account?.email || "—"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground-dim">Account ID</span>
              <span className="text-foreground text-[11px] truncate max-w-[200px]" title={account?.id}>
                {account?.id || "—"}
              </span>
            </div>
          </div>

          {/* Action Row: Primary Add Website + Refresh Check & Local Demo */}
          <div className="space-y-4 pt-1">
            {onAddWebsite && (
              <button
                type="button"
                data-testid="empty-state-add-website-btn"
                onClick={onAddWebsite}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-md bg-brand hover:bg-brand-hover text-white text-xs font-mono font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand shadow-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Add Website</span>
              </button>
            )}

            <div className="flex items-center justify-between pt-1">
              {onRefreshWebsites ? (
                <button
                  type="button"
                  onClick={onRefreshWebsites}
                  className="text-xs font-mono text-brand hover:underline focus-visible:outline-none"
                >
                  Check again
                </button>
              ) : <span />}

              {import.meta.env.DEV && onSwitchToDemo && (
                <button
                  type="button"
                  onClick={onSwitchToDemo}
                  className="text-xs font-mono text-foreground-muted hover:text-foreground transition-colors focus-visible:outline-none"
                >
                  View Local Demo Console →
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
